import { pdf } from '@react-pdf/renderer';
import { ModalContext } from 'app/app';
import axios from 'axios';
import { FieldInput } from 'components/fieldInput';
import { AuthContext, Fieldset, ViewContext } from 'components/lib';
import { Form, Formik } from 'formik';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import Certificate from './certificate';
import image from './images/loader.gif';
import styles from './styles.module.scss';

export function CertificatesModal() {
  const { id: company_id } = useParams();
  const context = useContext(ViewContext);
  const Auth = useContext(AuthContext);
  const { modal, setModal } = useContext(ModalContext);
  const [user, setUser] = useState({});
  const [companies, setCompanies] = useState(null);
  const [contactsPayload, setContactsPayload] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contact, setContact] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [submissionOptions, setSubmissionOptions] = useState([]);

  const firstValidationSchema = Yup.object().shape({
    companies: Yup.array().min(
      1,
      'You need to select at least one policy'
    ),
    contact: Yup.object().required(
      'You need to select at least one contact'
    ),
    certificate_holder: Yup.string().required(
      'This field is required!'
    ),
    send_to: Yup.string().required('This field is required!'),
  });

  async function runAPI() {
    const user = await axios.get('/api/user');
    const account = await axios.get('/api/account');
    setUser(user.data.data);
    setContactsPayload(account.data.data.certificate_contacts);
    setContacts(
      account.data.data.certificate_contacts
        ? account.data.data.certificate_contacts.map(
          (value, index) => {
            return {
              value: index,
              label: `${value.name} - ${value.email}`,
            };
          }
        )
        : []
    );

    if (!company_id && account.data.data.id) {
      await axios
        .get(
          `/api/quote/policies/all?account_id=${account.data.data.id}&status=active`
        )
        .then(async (res) => {
          const companies = [];

          res.data.data.data.forEach((value) => {
            if (
              !companies.find(
                (item) => item.company_id === value.company_id
              )
            ) {
              companies.push({
                company_id: value.company_id,
                company_name: value.company_name,
                policies: [value.submission_number],
              });
            } else {
              const companyIndex = companies.findIndex(
                (item) => item.company_id === value.company_id
              );

              companies[companyIndex].policies.push(
                value.submission_number
              );
            }
          });

          setOptions(
            companies.map((company) => {
              return {
                value: company.company_id,
                label: company.company_name,
                policies: company.policies,
              };
            })
          );
        })
        .catch((err) => {
          console.log(err);

          context.notification.show(
            'Failed to load policies.',
            'error',
            true,
            'toast',
            'x-circle'
          );
        });
    } else {
      setCompanies([company_id]);
    }

    setModalLoading(false);
  }

  const fetchSubmissionNumbers = async (companyId) => {
    const account = await axios.get('/api/account');
    const accountId = account.data.data.id;
    try {
      const response = await axios.get(
        `/api/quote/policies/all?account_id=${accountId}&company_id=${companyId}&bound_only=true`
      );

      if (response.data.success && response.data.data.data) {
        const submissions = response.data.data.data.map(quote => ({
          value: quote.submission_number,
          label: `${quote.submission_number} - ${quote.carrier || 'Unknown'}`,
        }));

        setSubmissionOptions(submissions);
      }
    } catch (error) {
      console.error('Failed to fetch submission numbers:', error);
      context.notification.show(
        'Failed to load submission numbers.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  };


  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const links = [];

      for (let i = 0; i < values.companies.length; i++) {
        let quoteData = {};
        let coverages = {};

        const company = values.companies[i];
        let agency;

        try {
          const agencyResponse = await axios.get('/api/account');
          agency = agencyResponse.data.data;
        } catch (err) {
          console.error("Error fetching agency data:", err);
          context.notification.show(
            'Error fetching agency data',
            'error',
            true,
            'toast',
            'x-circle'
          );
          setLoading(false);
          return;
        }

        const submissionNumbers = selectedSubmission
          ? [selectedSubmission.value]
          : company.policies;

        for (let a = 0; a < submissionNumbers.length; a++) {
          const submission_number = submissionNumbers[a];

          try {
            const res = await axios.get(`/api/quote/info/${submission_number}`);
            quoteData = res.data.data;

            if (
              !quoteData.insuredInformation &&
                quoteData.quote &&
                quoteData.quote.insuredInformation
            ) {
              quoteData.insuredInformation = quoteData.quote.insuredInformation;
            } else if (!quoteData.insuredInformation) {
              quoteData.insuredInformation = {
                legal_name: quoteData.carrier || 'Unknown',
                mailing_address: '',
                mailing_city: '',
                mailing_state: '',
                mailing_zip: ''
              };
            }

            if (!quoteData.coverages) {
              quoteData.coverages = {};
            }

            if (quoteData.coverages.al) {
              coverages.al = quoteData.coverages.al;
            }
            if (quoteData.coverages.tgl) {
              coverages.tgl = quoteData.coverages.tgl;
            }
            if (quoteData.coverages.mtc) {
              coverages.mtc = quoteData.coverages.mtc;
            }
            if (quoteData.coverages.apd) {
              coverages.apd = quoteData.coverages.apd;
            }
          } catch (err) {
            console.error("Error fetching quote data:", err);
            context.notification.show(
              'Error on create certificate, failed to get data at Cover Whale',
              'error',
              true,
              'toast',
              'x-circle'
            );
            continue;
          }
        }

        try {
          const pdfBlob = await pdf(
            <Certificate
              contact={contact}
              agency={agency}
              producer={contact.name}
              data={{ ...quoteData, coverages }}
              customerHolder={values.certificate_holder}
              signature={user.signature}
            />
          ).toBlob();

          const formData = new FormData();
          formData.append('file', pdfBlob, `certificate.pdf`);

          const cert_res = await axios.post('/api/certificate/file', formData);
          const companyIdToUse = modal.data && modal.data.company_id
            ? modal.data.company_id
            : values.companies[i].value;

          try {
            await axios.post('/api/certificate', {
              company_id: companyIdToUse,
              uploaded_by: Auth.user.name,
              submission_number: selectedSubmission
                ? selectedSubmission.value
                : company.policies.toString(),
              certificate_holder: values.certificate_holder,
              contact,
              link: cert_res.data.link,
              send_to: values.send_to,
            });

            links.push(cert_res.data.link);
          } catch (err) {
            console.error("Error creating certificate record:", err);
            context.notification.show(
              'Error on create certificate',
              'error',
              true,
              'toast',
              'x-circle'
            );
          }
        } catch (err) {
          console.error("Error generating or uploading certificate:", err);
          context.notification.show(
            'Error on upload certificate to server',
            'error',
            true,
            'toast',
            'x-circle'
          );
        }
      }

      links.forEach(link => {
        window.open(link);
      });

      setModal({});

      if (modal.action) {
        modal.action();
      }

      context.notification.show(
        'Certificate(s) created successfully!',
        'success',
        true,
        'toast',
        'check-circle'
      );
    } catch (error) {
      console.error('Certificate generation error:', error);
      context.notification.show(
        'An unexpected error occurred during certificate generation',
        'error',
        true,
        'toast',
        'x-circle'
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    runAPI();
  }, []);

  return (
    <Fragment>
      {modal.name === 'certificates_modal' &&
        (modalLoading ? (
          <div className={styles.background}>
            <div className={styles.wrapper}>
              <div
                className={styles.insideWrapper}
                style={{
                  height: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={image}
                  alt="loading"
                  style={{ width: '25px', borderRadius: 25 }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.background}>
            <div className={styles.wrapper}>
              <div className={styles.insideWrapper}>
                <div className={styles.title}>Create Certificate</div>
                <div className={styles.line} />
                <Formik
                  validationSchema={firstValidationSchema}
                  enableReinitialize
                  initialValues={{
                    companies,
                    contact: contacts[0] ? contacts[0] : null,
                    certificate_holder: '',
                    additional_remarks: '',
                    send_to: user.email,
                  }}
                  onSubmit={handleSubmit}
                >
                  {({
                    errors,
                    touched,
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    submitForm,
                  }) => (
                    <Fragment>
                      <Form
                        className={styles.form}
                        onSubmit={handleSubmit}
                      >
                        {!company_id && (
                          <div
                            className={styles.fieldRow}
                            style={{ marginBottom: 8 }}
                          >
                            <FieldInput
                              id="companies"
                              type="multi_select"
                              label="Companies"
                              defaultValue={companies}
                              handleChange={(event) => {
                                setCompanies(event);
                                if (event.length === 1) {
                                  fetchSubmissionNumbers(event[0].value);
                                } else {
                                  setSubmissionOptions([]);
                                }
                              }}
                              errors={errors.companies}
                              touched={touched.companies}
                              showErrors
                              required
                              options={options}
                            />
                          </div>
                        )}
                        {
                          companies &&
                          companies.length === 1 &&
                          submissionOptions.length > 0 && (
                            <div
                              className={styles.fieldRow}
                              style={{ marginBottom: 8 }}
                            >
                              <FieldInput
                                id="submission_number"
                                type="search_select"
                                label="Submission Number"
                                options={submissionOptions}
                                handleChange={(event) => {
                                  setSelectedSubmission(event);
                                }}
                                errors={errors.submission_number}
                                touched={touched.submission_number}
                                showErrors
                              />
                            </div>
                          )
                        }

                        <div
                          className={styles.fieldRow}
                          style={{ marginBottom: 8 }}
                        >
                          <FieldInput
                            id="contact"
                            type="search_select"
                            label="Contact"
                            options={contacts}
                            handleChange={(event) => {
                              setContact(
                                contactsPayload[event.value]
                              );
                            }}
                            errors={errors.contact}
                            touched={touched.contact}
                            showErrors
                            required
                          />
                        </div>
                        <div
                          className={styles.fieldRow}
                          style={{ marginBottom: 8 }}
                        >
                          <FieldInput
                            id="additional_remarks"
                            type="text"
                            label="Additional Remarks"
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors.additional_remarks}
                            touched={touched.additional_remarks}
                            showErrors
                          />
                        </div>
                        <div
                          className={styles.fieldRow}
                          style={{ marginBottom: 8 }}
                        >
                          <FieldInput
                            id="send_to"
                            type="text"
                            label="Send to"
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors.send_to}
                            touched={touched.send_to}
                            showErrors
                            required
                          />
                        </div>
                        <div
                          className={styles.fieldRow}
                          style={{ height: 270 }}
                        >
                          <FieldInput
                            id="certificate_holder"
                            type="area"
                            label="Certificate Holder"
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors.certificate_holder}
                            touched={touched.certificate_holder}
                            required
                            showErrors
                          />
                        </div>
                        {user.signature && (
                          <Fieldset
                            name="download_with_signature"
                            type="checkbox"
                            options={['Download with e-signature']}
                            cols={1}
                            onChange={handleChange}
                          />
                        )}
                      </Form>
                      <div className={styles.footer}>
                        <div className={styles.buttons}>
                          <button
                            className={styles.cancel}
                            onClick={() => setModal('')}
                          >
                            Cancel
                          </button>
                          <button
                            className={styles.save}
                            style={
                              !loading
                                ? { cursor: 'pointer' }
                                : { cursor: 'default' }
                            }
                            onClick={!loading ? submitForm : null}
                          >
                            {loading ? (
                              <img
                                src={image}
                                alt="loading"
                                style={{ width: '25px' }}
                              />
                            ) : (
                              'Generate'
                            )}
                          </button>
                        </div>
                      </div>
                    </Fragment>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        ))}
    </Fragment>
  );
}
